<template>
    <vx-expandable-card>
        <template v-slot:header>Connection</template>
        <template v-slot:content>
            <div class="flex flex-col gap-6">
                <page-loading v-if="loading" />

                <vx-alert v-else-if="loadFailed" color="danger" :closeable="false" data-test="load-failed">
                    <div class="flex items-center justify-between">
                        The connection could not be loaded.
                        <vx-button size="sm" color="danger" @click="loadConnection" data-test="retry-load">
                            Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                        </vx-button>
                    </div>
                </vx-alert>

                <template v-else>
                    <div>
                        <vx-label>
                            Protocol
                            <template v-if="connection" v-slot:description
                                >Remove the connection to switch protocols.</template
                            >
                        </vx-label>
                        <vx-radio-selection
                            v-model="form.protocol"
                            :disabled="connection !== null"
                            data-test="protocol"
                        >
                            <vx-radio-selection-item
                                v-for="option in PROTOCOL_OPTIONS"
                                :key="option.value"
                                :value="option.value"
                                :data-test="`protocol-${option.value}`"
                            >
                                <template v-slot:header>{{ option.label }}</template>
                                <template v-slot:content>{{ option.description }}</template>
                            </vx-radio-selection-item>
                        </vx-radio-selection>
                    </div>

                    <div>
                        <div class="font-bold mb-1">Details for your identity provider</div>
                        <div v-if="form.protocol === 'saml2'" class="text-sm text-slate-600 mb-3">
                            Create a SAML application in your identity provider (Microsoft Entra ID, Okta, Google
                            Workspace, AD FS) with these values.
                        </div>
                        <div v-else class="text-sm text-slate-600 mb-3">
                            Create an OpenID Connect web application in your identity provider (Microsoft Entra ID,
                            Okta, Google Workspace) with this redirect URI, then paste its client ID and secret below
                            and say how the app sends the secret; the preset fills that in.
                        </div>
                        <div class="grid gap-2 text-sm sm:grid-cols-[auto_1fr_auto] sm:items-center">
                            <template v-for="row in spRows" :key="row.key">
                                <span class="text-slate-500">{{ row.label }}</span>
                                <code class="break-all" :data-test="`sp-${row.key}`">{{ row.value }}</code>
                                <vx-button
                                    size="xs"
                                    color="muted"
                                    @click="copyText(row.value)"
                                    :data-test="`copy-sp-${row.key}`"
                                >
                                    Copy <font-awesome-icon widthAuto :icon="faCopy" />
                                </vx-button>
                            </template>
                        </div>
                    </div>

                    <div
                        v-if="connection"
                        class="flex items-center justify-between rounded-lg border border-slate-200 p-4"
                        data-test="connection-status"
                    >
                        <div>
                            <div class="font-bold">{{ form.enabled ? 'Enabled' : 'Disabled' }}</div>
                            <div class="text-sm text-slate-500">
                                <template v-if="connection.last_login_at"
                                    >Last SSO login {{ fromNow(connection.last_login_at) }}</template
                                >
                                <template v-else>No SSO login yet</template>
                            </div>
                        </div>
                        <vx-toggle v-model="form.enabled" size="md" data-test="enabled-toggle" />
                    </div>

                    <div>
                        <vx-label>Identity provider</vx-label>
                        <vx-selectable
                            :modelValue="preset ?? undefined"
                            @update:modelValue="applyPreset"
                            :options="form.protocol === 'saml2' ? IDP_PRESETS : OIDC_PRESETS"
                            label="label"
                            :reduce="(option: IdpPreset | OidcPreset) => option.key"
                            :placeholder="
                                form.protocol === 'saml2'
                                    ? 'Choose a preset to fill the attribute names'
                                    : 'Choose a preset to fill the scopes and claim names'
                            "
                            data-test="preset"
                        />
                    </div>

                    <template v-if="form.protocol === 'saml2'">
                        <vx-disclosure data-test="metadata-import">
                            <template v-slot:header>Paste identity provider metadata XML</template>
                            <template v-slot:content>
                                <vx-input
                                    v-model="metadataXml"
                                    :rows="6"
                                    size="lg"
                                    placeholder="<EntityDescriptor …>"
                                    data-test="metadata-xml"
                                    class="text-sm"
                                />
                                <div class="flex items-center gap-3 mt-2">
                                    <vx-button
                                        size="sm"
                                        color="muted-dark"
                                        @click="importMetadata"
                                        data-test="import-metadata"
                                        >Fill from metadata</vx-button
                                    >
                                    <span
                                        v-if="metadataError"
                                        class="text-xs text-rose-800"
                                        data-test="metadata-error"
                                        >{{ metadataError }}</span
                                    >
                                </div>
                            </template>
                        </vx-disclosure>

                        <div>
                            <vx-label>Identity provider entity ID</vx-label>
                            <vx-input
                                v-model="form.idp_entity_id"
                                size="lg"
                                data-test="idp-entity-id"
                                :error="displayFirstError(validation.idp_entity_id.$errors)"
                            />
                        </div>

                        <div>
                            <vx-label>
                                Identity provider login URL
                                <template v-slot:description
                                    >The HTTP-Redirect single sign-on URL; must start with https://</template
                                >
                            </vx-label>
                            <vx-input
                                v-model="form.idp_sso_url"
                                size="lg"
                                type="url"
                                data-test="idp-sso-url"
                                :error="displayFirstError(validation.idp_sso_url.$errors)"
                            />
                        </div>

                        <div>
                            <vx-label>
                                NameID format
                                <template v-slot:description>
                                    Persistent: a stable identifier, so an email change at the identity provider follows
                                    the same Voxie account. Email address or Unspecified: a changed email is a new
                                    identity.
                                </template>
                            </vx-label>
                            <vx-radio-selection v-model="form.name_id_format" data-test="name-id-format">
                                <vx-radio-selection-item
                                    v-for="option in NAME_ID_FORMAT_OPTIONS"
                                    :key="option.value"
                                    :value="option.value"
                                    :data-test="`name-id-format-${option.value}`"
                                >
                                    <template v-slot:header>{{ option.label }}</template>
                                </vx-radio-selection-item>
                            </vx-radio-selection>
                        </div>

                        <div class="flex flex-col gap-3">
                            <vx-label>
                                Signing certificates
                                <template v-slot:description>
                                    One to five X.509 certificates in PEM format. To rotate, add the new certificate
                                    next to the current one before activating it at the identity provider, then remove
                                    the old one.
                                </template>
                            </vx-label>
                            <div
                                v-for="(certificate, index) in form.certificates"
                                :key="certificate.key"
                                class="flex gap-2"
                                data-test="certificate"
                            >
                                <div class="grow">
                                    <vx-input
                                        v-model="certificate.value"
                                        :rows="4"
                                        size="lg"
                                        class="font-mono text-xs"
                                        :data-test="`certificate-${index}`"
                                        :error="displayFirstError(certificateErrors(index))"
                                    />
                                    <div
                                        v-if="savedCertificate(certificate.value)"
                                        class="text-xs mt-1"
                                        :class="certificateClass(savedCertificate(certificate.value)!)"
                                        :data-test="`certificate-expiry-${index}`"
                                    >
                                        {{ certificateSummary(savedCertificate(certificate.value)!) }}
                                    </div>
                                </div>
                                <vx-button
                                    v-if="form.certificates.length > 1"
                                    color="muted-light"
                                    class="w-12"
                                    @click="removeCertificate(index)"
                                    :data-test="`remove-certificate-${index}`"
                                >
                                    <font-awesome-icon :icon="faTrash" />
                                </vx-button>
                            </div>
                            <vx-button
                                v-if="form.certificates.length < MAX_CERTIFICATES"
                                size="sm"
                                color="muted-dark"
                                class="self-start"
                                @click="addCertificate"
                                data-test="add-certificate"
                            >
                                Add certificate <font-awesome-icon widthAuto :icon="faCirclePlus" />
                            </vx-button>
                        </div>

                        <div>
                            <vx-label>Email address comes from</vx-label>
                            <vx-radio-selection v-model="form.email_source" data-test="email-source">
                                <vx-radio-selection-item value="name_id" data-test="email-source-name-id">
                                    <template v-slot:header>The NameID</template>
                                    <template v-slot:content
                                        >The identity provider sends the email address as the subject NameID (Google
                                        Workspace default).</template
                                    >
                                </vx-radio-selection-item>
                                <vx-radio-selection-item value="attribute" data-test="email-source-attribute">
                                    <template v-slot:header>An attribute</template>
                                    <template v-slot:content
                                        >The identity provider sends the email address in a named attribute (Entra ID,
                                        Okta and AD FS default).</template
                                    >
                                </vx-radio-selection-item>
                            </vx-radio-selection>
                            <vx-input
                                v-if="form.email_source === 'attribute'"
                                v-model="form.email_attribute"
                                size="lg"
                                class="mt-2"
                                placeholder="email"
                                data-test="email-attribute"
                                :error="displayFirstError(validation.email_attribute.$errors)"
                            />
                        </div>

                        <div class="flex flex-col gap-2">
                            <vx-label>
                                Name attributes
                                <template v-slot:description
                                    >Up to three attributes joined with spaces to name a new user. Names are set once,
                                    when the account is created.</template
                                >
                            </vx-label>
                            <div
                                v-for="(attribute, index) in form.name_attributes"
                                :key="attribute.key"
                                class="flex gap-2"
                                data-test="name-attribute"
                            >
                                <vx-input
                                    v-model="attribute.value"
                                    size="lg"
                                    class="grow"
                                    :data-test="`name-attribute-${index}`"
                                />
                                <vx-button
                                    color="muted-light"
                                    class="w-12"
                                    @click="removeNameAttribute(index)"
                                    :data-test="`remove-name-attribute-${index}`"
                                >
                                    <font-awesome-icon :icon="faTrash" />
                                </vx-button>
                            </div>
                            <vx-button
                                v-if="form.name_attributes.length < MAX_NAME_ATTRIBUTES"
                                size="sm"
                                color="muted-dark"
                                class="self-start"
                                @click="addNameAttribute"
                                data-test="add-name-attribute"
                            >
                                Add attribute <font-awesome-icon widthAuto :icon="faCirclePlus" />
                            </vx-button>
                        </div>

                        <div class="flex items-center justify-between">
                            <div>
                                <vx-label>Allow login started from the identity provider</vx-label>
                                <div class="text-sm text-slate-500">
                                    Lets members open Voxie from their identity provider's app portal. Off by default.
                                </div>
                            </div>
                            <vx-toggle v-model="form.allow_idp_initiated" size="md" data-test="allow-idp-initiated" />
                        </div>
                    </template>

                    <template v-else>
                        <div>
                            <vx-label>
                                Issuer
                                <template v-slot:description
                                    >The issuer URL your identity provider publishes; must start with https://</template
                                >
                            </vx-label>
                            <vx-input
                                v-model="form.issuer"
                                size="lg"
                                type="url"
                                :placeholder="issuerPlaceholder"
                                data-test="issuer"
                                :error="displayFirstError(validation.issuer.$errors)"
                            />
                        </div>

                        <div>
                            <vx-label>Client ID</vx-label>
                            <vx-input
                                v-model="form.client_id"
                                size="lg"
                                data-test="client-id"
                                :error="displayFirstError(validation.client_id.$errors)"
                            />
                        </div>

                        <div>
                            <vx-label>Client secret</vx-label>
                            <vx-input
                                v-model="form.client_secret"
                                size="lg"
                                type="password"
                                togglePassword
                                :placeholder="clientSecretPlaceholder"
                                data-test="client-secret"
                                :error="displayFirstError(validation.client_secret.$errors)"
                            />
                        </div>

                        <div>
                            <vx-label>
                                Client authentication
                                <template v-slot:description
                                    >How the app was registered to send its secret. Choose the identity provider above
                                    to set it.</template
                                >
                            </vx-label>
                            <vx-radio-selection v-model="form.client_auth_method" data-test="client-auth-method">
                                <vx-radio-selection-item
                                    v-for="option in CLIENT_AUTH_METHOD_OPTIONS"
                                    :key="option.value"
                                    :value="option.value"
                                    :data-test="`client-auth-method-${option.value}`"
                                >
                                    <template v-slot:header>{{ option.label }}</template>
                                    <template v-slot:content>{{ option.description }}</template>
                                </vx-radio-selection-item>
                            </vx-radio-selection>
                        </div>

                        <div>
                            <vx-label>
                                Scopes
                                <template v-slot:description
                                    >Space-separated. openid is required; offline_access asks for the refresh token
                                    Voxie uses to keep checking the session (not for Google Workspace).</template
                                >
                            </vx-label>
                            <vx-input
                                v-model="form.scopes"
                                size="lg"
                                data-test="scopes"
                                :error="displayFirstError(validation.scopes.$errors)"
                            />
                        </div>

                        <div>
                            <vx-label>Email claim</vx-label>
                            <vx-input
                                v-model="form.email_claim"
                                size="lg"
                                placeholder="email"
                                data-test="email-claim"
                                :error="displayFirstError(validation.email_claim.$errors)"
                            />
                        </div>

                        <div class="flex flex-col gap-2">
                            <vx-label>
                                Name claims
                                <template v-slot:description
                                    >Up to three claims joined with spaces to name a new user. Names are set once, when
                                    the account is created.</template
                                >
                            </vx-label>
                            <div
                                v-for="(claim, index) in form.name_claims"
                                :key="claim.key"
                                class="flex gap-2"
                                data-test="name-claim"
                            >
                                <vx-input
                                    v-model="claim.value"
                                    size="lg"
                                    class="grow"
                                    :data-test="`name-claim-${index}`"
                                />
                                <vx-button
                                    color="muted-light"
                                    class="w-12"
                                    @click="removeNameClaim(index)"
                                    :data-test="`remove-name-claim-${index}`"
                                >
                                    <font-awesome-icon :icon="faTrash" />
                                </vx-button>
                            </div>
                            <vx-button
                                v-if="form.name_claims.length < MAX_NAME_CLAIMS"
                                size="sm"
                                color="muted-dark"
                                class="self-start"
                                @click="addNameClaim"
                                data-test="add-name-claim"
                            >
                                Add claim <font-awesome-icon widthAuto :icon="faCirclePlus" />
                            </vx-button>
                        </div>
                    </template>

                    <div>
                        <vx-label>
                            Session lifetime
                            <template v-slot:description
                                >Between 15 minutes and 8 hours (480). Members log in again with SSO after that, or
                                sooner if the identity provider limits the session. For OpenID Connect this is also how
                                long a removed user can keep using Voxie before the next check logs them out.</template
                            >
                        </vx-label>
                        <vx-input
                            v-model="form.session_lifetime_minutes"
                            type="number"
                            size="lg"
                            :min="SESSION_LIFETIME_MINUTES_MIN"
                            :max="SESSION_LIFETIME_MINUTES_MAX"
                            class="max-w-[200px]"
                            data-test="session-lifetime"
                            :error="displayFirstError(validation.session_lifetime_minutes.$errors)"
                        >
                            <template v-slot:right>minutes</template>
                        </vx-input>
                    </div>

                    <vx-alert v-if="conflict" color="danger" :closeable="false" data-test="conflict">{{
                        conflict
                    }}</vx-alert>

                    <div class="flex flex-col gap-2 sm:flex-row sm:justify-between">
                        <vx-button
                            size="lg"
                            color="primary"
                            :loading="saving"
                            @click="save"
                            data-test="save-connection"
                        >
                            {{ connection ? 'Save changes' : 'Connect' }}
                        </vx-button>
                        <vx-button
                            v-if="connection"
                            size="lg"
                            color="danger"
                            outline
                            @click="removeConnection"
                            data-test="remove-connection"
                        >
                            Remove connection <font-awesome-icon widthAuto :icon="faTrash" />
                        </vx-button>
                    </div>
                </template>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { faCirclePlus, faCopy, faRefresh, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxButton,
    VxDisclosure,
    VxExpandableCard,
    VxInput,
    VxLabel,
    VxRadioSelection,
    VxRadioSelectionItem,
    VxSelectable,
    VxSwal,
    VxSwalError,
    VxToggle,
} from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { between, helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useCopyText } from '~/composables/useCopyText';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';
import { ssoClient } from '~/services';
import type {
    SsoCertificate,
    SsoClientAuthMethod,
    SsoConfig,
    SsoConnection,
    SsoConnectionInput,
    SsoEmailSource,
    SsoNameIdFormat,
    SsoProtocol,
} from '~/services/ssoClient.types';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { fromNow, shortDate } from '~/utils/date';
import incrementingId from '~/utils/incrementingId';
import { throwSwalRequestError } from '~/utils/request';
import { displayFirstError, validateRules } from '~/utils/validation';
import {
    CLIENT_AUTH_METHOD_OPTIONS,
    IDP_PRESETS,
    MAX_CERTIFICATES,
    MAX_NAME_ATTRIBUTES,
    MAX_NAME_CLAIMS,
    MAX_SCOPES,
    NAME_ID_FORMAT_OPTIONS,
    OIDC_PRESETS,
    PROTOCOL_OPTIONS,
    SESSION_LIFETIME_MINUTES_DEFAULT,
    SESSION_LIFETIME_MINUTES_MAX,
    SESSION_LIFETIME_MINUTES_MIN,
    certificateState,
    certificateStateClass,
    conflictText,
    parseIdpMetadata,
    parseScopes,
    type IdpPreset,
    type OidcPreset,
} from './ssoSettingsHelpers';

interface KeyedValue {
    key: number;
    value: string;
}

const props = defineProps<{
    teamId: number;
    config: SsoConfig;
}>();

const emit = defineEmits<{
    changed: [];
}>();

const toasted = useToasted();
const { copyText } = useCopyText();
const { $externalResults, catchError } = useFormRequestHandler();

const connection = ref<SsoConnection | null>(null);
const loading = ref(true);
const loadFailed = ref(false);
const saving = ref(false);
const preset = ref<IdpPreset['key'] | OidcPreset['key'] | null>(null);
const metadataXml = ref('');
const metadataError = ref('');
const conflict = ref<string | null>(null);

const keyed = (value = ''): KeyedValue => ({ key: incrementingId(), value });

const defaultForm = () => ({
    protocol: 'saml2' as SsoProtocol,
    enabled: true,
    idp_entity_id: '',
    idp_sso_url: '',
    name_id_format: 'unspecified' as SsoNameIdFormat,
    certificates: [keyed()],
    email_source: 'attribute' as SsoEmailSource,
    email_attribute: '',
    name_attributes: [] as KeyedValue[],
    allow_idp_initiated: false,
    issuer: '',
    client_id: '',
    client_secret: '',
    client_auth_method: 'client_secret_post' as SsoClientAuthMethod,
    scopes: '',
    email_claim: '',
    name_claims: [] as KeyedValue[],
    session_lifetime_minutes: SESSION_LIFETIME_MINUTES_DEFAULT,
});

const form = reactive(defaultForm());

const isSaml = () => form.protocol === 'saml2';
const isOidc = () => form.protocol === 'oidc';

const validation = useVuelidate(
    {
        idp_entity_id: {
            required: requiredIf(isSaml),
            maxLength: maxLength(1024),
            noWhitespace: helpers.withMessage('The entity ID cannot contain spaces.', helpers.regex(/^\S+$/)),
        },
        idp_sso_url: {
            required: requiredIf(isSaml),
            https: helpers.withMessage('The login URL must start with https://', helpers.regex(/^https:\/\/\S+$/)),
        },
        email_attribute: {
            required: requiredIf(() => isSaml() && form.email_source === 'attribute'),
            maxLength: maxLength(191),
        },
        issuer: {
            required: requiredIf(isOidc),
            https: helpers.withMessage('The issuer must start with https://', helpers.regex(/^https:\/\/\S+$/)),
            maxLength: maxLength(2048),
        },
        client_id: { required: requiredIf(isOidc), maxLength: maxLength(255) },
        client_secret: { required: requiredIf(() => isOidc() && connection.value === null) },
        scopes: {
            required: requiredIf(isOidc),
            maxScopes: helpers.withMessage(
                'Up to ten scopes.',
                (value: string) => parseScopes(value).length <= MAX_SCOPES
            ),
        },
        email_claim: { required: requiredIf(isOidc), maxLength: maxLength(191) },
        session_lifetime_minutes: {
            required,
            between: between(SESSION_LIFETIME_MINUTES_MIN, SESSION_LIFETIME_MINUTES_MAX),
        },
    },
    form,
    { $externalResults }
);

const spRows = computed(() =>
    isSaml()
        ? [
              { key: 'entity-id', label: 'Entity ID', value: props.config.sp.entity_id },
              { key: 'metadata-url', label: 'Metadata URL', value: props.config.sp.metadata_url },
              { key: 'acs-url', label: 'ACS URL', value: props.config.sp.acs_url },
              { key: 'login-url', label: 'Login URL', value: props.config.sp.login_url },
          ]
        : [
              { key: 'redirect-uri', label: 'Redirect URI', value: props.config.oidc.redirect_uri },
              { key: 'login-url', label: 'Login URL', value: props.config.oidc.login_url },
          ]
);

const issuerPlaceholder = computed(
    () => OIDC_PRESETS.find((candidate) => candidate.key === preset.value)?.issuer_placeholder
);

const clientSecretPlaceholder = computed(() =>
    connection.value?.client_secret_set ? 'Leave empty to keep the current secret' : undefined
);

const fillForm = (source: SsoConnection) => {
    form.protocol = source.protocol;
    form.enabled = source.enabled;
    form.session_lifetime_minutes = source.session_lifetime_minutes;

    if (source.protocol === 'saml2') {
        form.idp_entity_id = source.idp_entity_id;
        form.idp_sso_url = source.idp_sso_url;
        form.name_id_format = source.name_id_format;
        form.certificates = source.certificates.map((certificate) => keyed(certificate.certificate));
        form.email_source = source.email_source;
        form.email_attribute = source.email_attribute ?? '';
        form.name_attributes = source.name_attributes.map((attribute) => keyed(attribute));
        form.allow_idp_initiated = source.allow_idp_initiated;
    } else {
        form.issuer = source.issuer;
        form.client_id = source.client_id;
        form.client_secret = '';
        form.client_auth_method = source.client_auth_method;
        form.scopes = source.scopes.join(' ');
        form.email_claim = source.email_claim;
        form.name_claims = source.name_claims.map((claim) => keyed(claim));
    }
};

const loadConnection = async () => {
    loading.value = true;
    loadFailed.value = false;

    try {
        const { data } = await ssoClient.getConnection(props.teamId);
        connection.value = data;
        fillForm(data);
    } catch (error) {
        if ((error as { response?: { status?: number } })?.response?.status !== HTTP_NOT_FOUND) {
            loadFailed.value = true;
        }
    } finally {
        loading.value = false;
    }
};

// fillForm() sets the protocol of a loaded connection; only a switch on a new one resets the form.
watch(
    () => form.protocol,
    (protocol) => {
        if (connection.value === null) {
            Object.assign(form, defaultForm(), { protocol });
            preset.value = null;
            validation.value.$reset();
        }
    }
);

const applyPreset = (key: IdpPreset['key'] | OidcPreset['key'] | null) => {
    preset.value = key;

    if (isSaml()) {
        const chosen = IDP_PRESETS.find((candidate) => candidate.key === key);

        if (chosen === undefined) {
            return;
        }

        form.name_id_format = chosen.name_id_format;
        form.email_source = chosen.email_source;
        form.email_attribute = chosen.email_attribute ?? '';
        form.name_attributes = chosen.name_attributes.map((attribute) => keyed(attribute));

        return;
    }

    const chosen = OIDC_PRESETS.find((candidate) => candidate.key === key);

    if (chosen === undefined) {
        return;
    }

    form.scopes = chosen.scopes.join(' ');
    form.client_auth_method = chosen.client_auth_method;
    form.email_claim = chosen.email_claim;
    form.name_claims = chosen.name_claims.map((claim) => keyed(claim));
};

const importMetadata = () => {
    const parsed = parseIdpMetadata(metadataXml.value);

    if (parsed === null) {
        metadataError.value =
            'Paste the metadata of one SAML identity provider that offers an HTTP-Redirect login URL.';
        return;
    }

    metadataError.value = '';
    form.idp_entity_id = parsed.idp_entity_id;
    form.idp_sso_url = parsed.idp_sso_url;
    form.certificates = parsed.certificates.length
        ? parsed.certificates.map((certificate) => keyed(certificate))
        : [keyed()];
};

const savedCertificate = (pem: string): SsoCertificate | undefined =>
    connection.value?.certificates?.find((certificate) => certificate.certificate === pem);

const certificateSummary = (certificate: SsoCertificate): string => {
    const state = certificateState(certificate);
    const expiry = shortDate(certificate.not_after);

    if (state === 'expired') {
        return `Expired on ${expiry}`;
    }

    return `${state === 'expiring' ? 'Expires soon: ' : 'Valid until '}${expiry} · ${certificate.subject}`;
};

const certificateClass = (certificate: SsoCertificate): string => certificateStateClass[certificateState(certificate)];

const certificateErrors = (index: number) => $externalResults.value[`certificates.${index}`] ?? [];

const addCertificate = () => form.certificates.push(keyed());
const removeCertificate = (index: number) => form.certificates.splice(index, 1);
const addNameAttribute = () => form.name_attributes.push(keyed());
const removeNameAttribute = (index: number) => form.name_attributes.splice(index, 1);
const addNameClaim = () => form.name_claims.push(keyed());
const removeNameClaim = (index: number) => form.name_claims.splice(index, 1);

const payload = (): SsoConnectionInput =>
    isSaml()
        ? {
              protocol: 'saml2',
              enabled: form.enabled,
              idp_entity_id: form.idp_entity_id.trim(),
              idp_sso_url: form.idp_sso_url.trim(),
              name_id_format: form.name_id_format,
              certificates: form.certificates.map((certificate) => certificate.value.trim()),
              email_source: form.email_source,
              email_attribute: form.email_source === 'attribute' ? form.email_attribute.trim() : null,
              name_attributes: form.name_attributes
                  .map((attribute) => attribute.value.trim())
                  .filter((value) => value !== ''),
              allow_idp_initiated: form.allow_idp_initiated,
              session_lifetime_minutes: Number(form.session_lifetime_minutes),
          }
        : {
              protocol: 'oidc',
              enabled: form.enabled,
              issuer: form.issuer.trim(),
              client_id: form.client_id.trim(),
              client_secret: form.client_secret === '' ? null : form.client_secret,
              client_auth_method: form.client_auth_method,
              scopes: parseScopes(form.scopes),
              email_claim: form.email_claim.trim(),
              name_claims: form.name_claims.map((claim) => claim.value.trim()).filter((value) => value !== ''),
              session_lifetime_minutes: Number(form.session_lifetime_minutes),
          };

const save = async () => {
    if (saving.value || !(await validateRules(validation))) {
        return;
    }

    if (isSaml()) {
        form.certificates = form.certificates.filter((certificate) => certificate.value.trim() !== '');

        if (form.certificates.length === 0) {
            form.certificates = [keyed()];
            $externalResults.value = { 'certificates.0': ['Add at least one signing certificate.'] };
            return;
        }
    }

    const input = payload();

    if (input.protocol === 'oidc' && !input.scopes.includes('openid')) {
        $externalResults.value = { scopes: ['The openid scope is required.'] };
        return;
    }

    saving.value = true;
    conflict.value = null;

    try {
        const { data } = await ssoClient.updateConnection(props.teamId, input);
        const created = connection.value === null;
        connection.value = data;
        fillForm(data);
        toasted.success(created ? 'Identity provider connected.' : 'Connection saved.');
        emit('changed');
    } catch (error) {
        conflict.value = conflictText(error);

        if (conflict.value === null) {
            catchError(error);
        }
    } finally {
        saving.value = false;
    }
};

const removeConnection = () => {
    VxSwal.confirmation(
        'Remove connection',
        'Members will no longer be able to log in with SSO and their SSO identities will be unlinked. Any provisioning token is deleted, so your identity provider can no longer add members or revoke their single sign-on until you generate a new one. If you connect an identity provider again, each member is claimed again at their next SSO login (password reset, tokens and devices revoked). There is no undo.',
        'danger',
        () =>
            ssoClient
                .deleteConnection(props.teamId)
                .then(() => {
                    connection.value = null;
                    Object.assign(form, defaultForm());
                    preset.value = null;
                    emit('changed');
                })
                .catch((error) => {
                    const text = conflictText(error);

                    if (text !== null) {
                        throw new VxSwalError(text);
                    }

                    throwSwalRequestError(error);
                })
    );
};

onMounted(loadConnection);
</script>
